import { rootApi } from '@components/rootApi';
import {
  CpgReport,
  CpgRetailer,
  CpgReportsResponse,
  cpgReportsResponse,
} from './schema';
import { convertParamsToSnakeCase } from '@utils/api';
import {
  Campaign,
  GetCampaignParams,
  GetCampaignsParams,
  campaignSchema,
  campaignsResponseSchema,
  CampaignsResponseSchema,
  UpdateCampaignParams,
} from '@features/campaigns';
import { CPG_REPORT_TAG } from './tags';

export type CpgReportsParams = {
  campaignId: number;
  includeUnapproved: boolean;
  showRestrictedValues: boolean;
  channel: string | null;
  showHiddenRetailers?: boolean;
};

const cpgApi = rootApi.injectEndpoints({
  endpoints: builder => ({
    getCpgReports: builder.query<CpgReportsResponse, CpgReportsParams>({
      query: params => {
        return {
          url: `/api/v3/cpg/campaigns/${params.campaignId}/reports`,
          params: convertParamsToSnakeCase({
            ...params,
            // Coerce null to undefined to avoid sending the param
            channel: params.channel ?? undefined,
          }),
        };
      },
      transformResponse: (response: CpgReportsResponse) => {
        return cpgReportsResponse.parse(response);
      },
      providesTags: result =>
        result?.data.reports.map(({ id }) => ({ type: CPG_REPORT_TAG, id })) ??
        [],
    }),

    updateCpgReport: builder.mutation<
      void,
      {
        report: Pick<CpgReport, 'id' | 'campaignId' | 'status'>;
        campaignsParams: GetCampaignsParams;
        reportsParams: CpgReportsParams;
        isLatestReport: boolean;
        channel: string | null;
      }
    >({
      query: ({ report: { id, campaignId, ...body } }) => {
        return {
          url: `/api/v3/cpg/campaigns/${campaignId}/reports/${id}`,
          method: 'PATCH',
          body,
        };
      },
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const { campaignsParams, report, reportsParams } = params;

        const getReportsResult = dispatch(
          cpgApi.util.updateQueryData('getCpgReports', reportsParams, draft => {
            const reportIndex = draft.data.reports.findIndex(
              r => r.id === report.id
            );

            const draftReport = draft.data.reports[reportIndex];

            if (!draftReport) return;

            if (report.status) {
              draftReport.status = report.status;
            }
          })
        );

        const getCampaignsResult = campaignsParams
          ? [
              dispatch(
                cpgApi.util.updateQueryData(
                  'getCpgCampaigns',
                  campaignsParams,
                  draft => {
                    // Only update the campaign status for the latest weekly report
                    if (!params.isLatestReport || params.channel !== null)
                      return;

                    const campaign = draft.campaigns.find(
                      c => c.id === report.campaignId
                    );
                    if (!campaign) return;

                    if (report.status) {
                      campaign.latestReportStatus = report.status;
                    }
                  }
                )
              ),
            ]
          : [];

        const results = [getReportsResult, ...getCampaignsResult];

        try {
          await queryFulfilled;
        } catch (e) {
          console.error(e);
          results.forEach(result => result.undo());
        }
      },
    }),

    getCpgCampaign: builder.query<Campaign, GetCampaignParams>({
      query: ({ id, ...params }) => ({
        url: `/api/v3/cpg/campaigns/${id}`,
        params,
      }),
      transformResponse: (response: Campaign) => campaignSchema.parse(response),
    }),

    getCpgCampaigns: builder.query<CampaignsResponseSchema, GetCampaignsParams>(
      {
        query: params => ({
          url: '/api/v3/cpg/campaigns',
          params,
        }),
        transformResponse: (response: CampaignsResponseSchema) =>
          campaignsResponseSchema.parse(response),
      }
    ),

    getCpgReportDates: builder.query<string[], void>({
      query: () => ({
        url: '/api/v3/cpg/campaigns/report_dates',
      }),
    }),

    updateCpgCampaign: builder.mutation<Campaign, UpdateCampaignParams>({
      query: ({ id, getCampaignsParams: _sideEffectParams, ...body }) => ({
        url: `/api/v3/cpg/campaigns/${id}`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const result = dispatch(
          cpgApi.util.updateQueryData(
            'getCpgCampaigns',
            params.getCampaignsParams,
            draft => {
              const campaignIndex = draft.campaigns.findIndex(
                c => c.id === params.id
              );
              const campaign = draft.campaigns[campaignIndex];

              if (!campaign) {
                console.warn('Campaign not found in cache');
                return;
              }

              if (params.archived !== undefined)
                campaign.archived = params.archived;
            }
          )
        );

        try {
          await queryFulfilled;
        } catch (e) {
          console.error(e);
          result.undo();
        }
      },
    }),

    bulkUpdateCpgReports: builder.mutation<
      void,
      {
        campaignId: number;
        reportDate: string;
        status: 'approved' | 'pending';
      }
    >({
      query: ({ campaignId, ...body }) => ({
        url: `/api/v3/cpg/campaigns/${campaignId}/reports/bulk_update`,
        method: 'POST',
        body: convertParamsToSnakeCase(body),
      }),
      invalidatesTags: [{ type: CPG_REPORT_TAG }],
    }),

    updateRetailer: builder.mutation<
      { data: { retailer: CpgRetailer } },
      {
        retailerId: number;
        hidden: boolean;
      }
    >({
      query: ({ retailerId, ...body }) => ({
        url: `/api/v3/cpg/retailers/${retailerId}`,
        method: 'PATCH',
        body: convertParamsToSnakeCase(body),
      }),
      invalidatesTags: [{ type: CPG_REPORT_TAG }],
    }),
  }),
});

export const {
  useGetCpgReportsQuery,
  useUpdateCpgReportMutation,
  useGetCpgCampaignQuery,
  useGetCpgCampaignsQuery,
  useGetCpgReportDatesQuery,
  useUpdateCpgCampaignMutation,
  useBulkUpdateCpgReportsMutation,
  useUpdateRetailerMutation,
} = cpgApi;
